import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../components/ui/Toast';
import { api } from '../services/api';

const complaintSchema = z.object({
  fullName: z.string().min(2, 'Full name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  product: z.string().min(1, 'Please select a product'),
  batch: z.string().min(1, 'Please select a batch'),
  defect_description: z.string().min(10, 'Description must be at least 10 characters'),
  severity: z.enum(['Low', 'Medium', 'High', 'Critical']),
  business_impact: z.enum(['Material Waste', 'Production Delay', 'Customer Dissatisfaction', 'Safety Risk', 'Financial Loss']),
  cost_impact_estimate: z.string().min(1, 'Cost impact estimate is required'),
});

type ComplaintFormData = z.infer<typeof complaintSchema>;

interface Product {
  product_id: number;
  name: string;
}

interface Batch {
  batch_id: number;
  batch_number: string;
  production_date: string;
}

export default function HomePage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ComplaintFormData>({
    resolver: zodResolver(complaintSchema),
  });

  const watchedDescription = watch('defect_description', '');

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedProduct) {
      fetchBatches(selectedProduct.product_id.toString());
    } else {
      setBatches([]);
    }
  }, [selectedProduct]);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      const productsData = response.data.data.products; // Access the nested products array
      // Ensure products is always an array
      if (Array.isArray(productsData)) {
        setProducts(productsData);
      } else {
        console.error('Products data is not an array:', productsData);
        setProducts([]);
        addToast({
          title: 'Warning',
          message: 'No products available',
          type: 'warning',
        });
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]); // Ensure products is always an array
      addToast({
        title: 'Error',
        message: 'Failed to load products',
        type: 'error',
      });
    }
  };

  const fetchBatches = async (productId: string) => {
    try {
      const response = await api.get(`/products/${productId}/batches`);
      const batchesData = response.data.data;
      // Ensure batches is always an array
      if (Array.isArray(batchesData)) {
        setBatches(batchesData);
      } else {
        console.error('Batches data is not an array:', batchesData);
        setBatches([]);
        addToast({
          title: 'Warning',
          message: 'No batches available for this product',
          type: 'warning',
        });
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
      setBatches([]); // Ensure batches is always an array
      addToast({
        title: 'Error',
        message: 'Failed to load batches',
        type: 'error',
      });
    }
  };

  const onSubmit = async (data: ComplaintFormData) => {
    setIsSubmitting(true);
    try {
      // Find the selected product and batch
      const product = products.find(p => p.name === data.product);
      const batch = batches.find(b => b.batch_number === data.batch);

      if (!product || !batch) {
        addToast({
          title: 'Error',
          message: 'Please select valid product and batch',
          type: 'error',
        });
        return;
      }

      const submitData = {
        fullName: data.fullName,
        email: data.email,
        product_id: product.product_id,
        batch_id: batch.batch_id,
        defect_description: data.defect_description,
        severity: data.severity,
        business_impact: data.business_impact,
        cost_impact_estimate: data.cost_impact_estimate,
      };

      await api.post('/complaints', submitData);
      addToast({
        title: 'Success',
        message: 'Your complaint has been submitted successfully',
        type: 'success',
      });
      navigate('/thank-you');
    } catch (error: any) {
      addToast({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to submit complaint',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="bg-white shadow-2xl border-0 rounded-3xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white p-8">
              <div className="text-center">
                <CardTitle className="text-3xl font-bold mb-2">Customer Complaint Form</CardTitle>
                <p className="text-blue-100 text-lg">Please fill out all required fields to submit a customer complaint</p>
              </div>
            </CardHeader>
            <CardContent className="p-8 bg-gradient-to-b from-white to-gray-50">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                {/* Customer Information Section */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-bold text-sm">1</span>
                    </div>
                    Customer Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label htmlFor="fullName" className="text-sm font-semibold text-gray-800">
                        Customer Name *
                      </Label>
                      <Input
                        id="fullName"
                        placeholder="Enter customer name"
                        {...register('fullName')}
                        className="h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl shadow-sm"
                      />
                      {errors.fullName && (
                        <p className="text-sm text-red-600 flex items-center mt-1">
                          <span className="mr-1">⚠</span>
                          {errors.fullName.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-3">
                      <Label htmlFor="email" className="text-sm font-semibold text-gray-800">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter Email information"
                        {...register('email')}
                        className="h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl shadow-sm"
                      />
                      {errors.email && (
                        <p className="text-sm text-red-600 flex items-center mt-1">
                          <span className="mr-1">⚠</span>
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Product Information Section */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-green-600 font-bold text-sm">2</span>
                    </div>
                    Product Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label className="text-sm font-semibold text-gray-800">Product *</Label>
                      <Select
                        onValueChange={(value) => {
                          const product = products.find(p => p.name === value);
                          setSelectedProduct(product || null);
                          setValue('product', value);
                          setValue('batch', ''); // Reset batch selection
                        }}
                      >
                        <SelectTrigger className="h-12 border-gray-200 focus:border-green-500 focus:ring-green-500 rounded-xl shadow-sm">
                          <SelectValue placeholder="Select a product" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl shadow-xl border-gray-200">
                          {Array.isArray(products) && products.length > 0 ? (
                            products.map((product) => (
                              <SelectItem
                                key={product.product_id}
                                value={product.name}
                                className="rounded-lg hover:bg-green-50"
                              >
                                {product.name}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="no-products" disabled className="text-gray-500">
                              No products available
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      {errors.product && (
                        <p className="text-sm text-red-600 flex items-center mt-1">
                          <span className="mr-1">⚠</span>
                          {errors.product.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-3">
                      <Label className="text-sm font-semibold text-gray-800">Batch *</Label>
                      <Select
                        onValueChange={(value) => setValue('batch', value)}
                        disabled={!selectedProduct}
                      >
                        <SelectTrigger className="h-12 border-gray-200 focus:border-green-500 focus:ring-green-500 rounded-xl shadow-sm disabled:bg-gray-50">
                          <SelectValue placeholder={selectedProduct ? "Select a batch" : "Select product first"} />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl shadow-xl border-gray-200">
                          {Array.isArray(batches) && batches.length > 0 ? (
                            batches.map((batch) => (
                              <SelectItem
                                key={batch.batch_id}
                                value={batch.batch_number}
                                className="rounded-lg hover:bg-green-50"
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{batch.batch_number}</span>
                                  <span className="text-xs text-gray-500">
                                    Production: {new Date(batch.production_date).toLocaleDateString()}
                                  </span>
                                </div>
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="no-batches" disabled className="text-gray-500">
                              {selectedProduct ? 'No batches available' : 'Select product first'}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      {errors.batch && (
                        <p className="text-sm text-red-600 flex items-center mt-1">
                          <span className="mr-1">⚠</span>
                          {errors.batch.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Complaint Details Section */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-orange-600 font-bold text-sm">3</span>
                    </div>
                    Complaint Details
                  </h3>
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <Label htmlFor="defect_description" className="text-sm font-semibold text-gray-800">
                        Defect Description *
                      </Label>
                      <Textarea
                        id="defect_description"
                        placeholder="Describe the defect in detail (minimum 10 characters)"
                        {...register('defect_description')}
                        className="min-h-[120px] resize-none border-gray-200 focus:border-orange-500 focus:ring-orange-500 rounded-xl shadow-sm"
                      />
                      <div className="flex justify-between items-center">
                        {errors.defect_description && (
                          <p className="text-sm text-red-600 flex items-center">
                            <span className="mr-1">⚠</span>
                            {errors.defect_description.message}
                          </p>
                        )}
                        <p className="text-sm text-gray-500 ml-auto bg-gray-100 px-2 py-1 rounded-lg">
                          {watchedDescription.length}/1000
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Impact Assessment Section */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-red-600 font-bold text-sm">4</span>
                    </div>
                    Impact Assessment
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label className="text-sm font-semibold text-gray-800">Severity Level *</Label>
                      <Select onValueChange={(value) => setValue('severity', value as any)}>
                        <SelectTrigger className="h-12 border-gray-200 focus:border-red-500 focus:ring-red-500 rounded-xl shadow-sm">
                          <SelectValue placeholder="Select severity level" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl shadow-xl border-gray-200">
                          <SelectItem value="Low" className="rounded-lg hover:bg-green-50">
                            <div className="flex items-center">
                              <div className="w-3 h-3 rounded-full bg-green-500 mr-3"></div>
                              <div>
                                <div className="font-medium">Low</div>
                                <div className="text-xs text-gray-500">Minor issue</div>
                              </div>
                            </div>
                          </SelectItem>
                          <SelectItem value="Medium" className="rounded-lg hover:bg-yellow-50">
                            <div className="flex items-center">
                              <div className="w-3 h-3 rounded-full bg-yellow-500 mr-3"></div>
                              <div>
                                <div className="font-medium">Medium</div>
                                <div className="text-xs text-gray-500">Moderate issue</div>
                              </div>
                            </div>
                          </SelectItem>
                          <SelectItem value="High" className="rounded-lg hover:bg-orange-50">
                            <div className="flex items-center">
                              <div className="w-3 h-3 rounded-full bg-orange-500 mr-3"></div>
                              <div>
                                <div className="font-medium">High</div>
                                <div className="text-xs text-gray-500">Significant issue</div>
                              </div>
                            </div>
                          </SelectItem>
                          <SelectItem value="Critical" className="rounded-lg hover:bg-red-50">
                            <div className="flex items-center">
                              <div className="w-3 h-3 rounded-full bg-red-500 mr-3"></div>
                              <div>
                                <div className="font-medium">Critical</div>
                                <div className="text-xs text-gray-500">Urgent issue</div>
                              </div>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.severity && (
                        <p className="text-sm text-red-600 flex items-center mt-1">
                          <span className="mr-1">⚠</span>
                          {errors.severity.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-3">
                      <Label className="text-sm font-semibold text-gray-800">Business Impact *</Label>
                      <Select onValueChange={(value) => setValue('business_impact', value as any)}>
                        <SelectTrigger className="h-12 border-gray-200 focus:border-red-500 focus:ring-red-500 rounded-xl shadow-sm">
                          <SelectValue placeholder="Select business impact" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl shadow-xl border-gray-200">
                          <SelectItem value="Material Waste" className="rounded-lg hover:bg-red-50">Material Waste</SelectItem>
                          <SelectItem value="Production Delay" className="rounded-lg hover:bg-red-50">Production Delay</SelectItem>
                          <SelectItem value="Customer Dissatisfaction" className="rounded-lg hover:bg-red-50">Customer Dissatisfaction</SelectItem>
                          <SelectItem value="Safety Risk" className="rounded-lg hover:bg-red-50">Safety Risk</SelectItem>
                          <SelectItem value="Financial Loss" className="rounded-lg hover:bg-red-50">Financial Loss</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.business_impact && (
                        <p className="text-sm text-red-600 flex items-center mt-1">
                          <span className="mr-1">⚠</span>
                          {errors.business_impact.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 space-y-3">
                    <Label htmlFor="cost_impact_estimate" className="text-sm font-semibold text-gray-800">
                      Cost Impact Estimate (₹) *
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">₹</span>
                      <Input
                        id="cost_impact_estimate"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...register('cost_impact_estimate')}
                        className="h-12 pl-8 border-gray-200 focus:border-red-500 focus:ring-red-500 rounded-xl shadow-sm"
                      />
                    </div>
                    {errors.cost_impact_estimate && (
                      <p className="text-sm text-red-600 flex items-center mt-1">
                        <span className="mr-1">⚠</span>
                        {errors.cost_impact_estimate.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Submit Section */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 shadow-xl">
                  <div className="text-center mb-4">
                    <h4 className="text-white font-semibold text-lg">Ready to Submit?</h4>
                    <p className="text-blue-100 text-sm">Please review your information before submitting</p>
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 text-lg font-semibold bg-white text-blue-600 hover:bg-gray-50 transition-all duration-200 rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                        Submitting Complaint...
                      </div>
                    ) : (
                      'Submit Complaint'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}